'use client';

/**
 * EdgeStrip — thin right-edge column hosting rail-surface summon icons.
 *
 * Phase 1 of the right-rail UX direction: hosts four icons —
 * Insights, Copilot, Chat, Composer. All four are peer surfaces
 * inside the right rail column (one visible at a time, all always
 * mounted; click swaps active surface and ensures the rail is open).
 *
 * Chat is matrix-gated per (mode, selection) via `chat_surface_cards`.
 * For modes that haven't migrated yet, the chat icon falls back to
 * the legacy `<ChatDrawer />` overlay (`chatDrawerOpen` toggle) so
 * chat access never disappears mid-cohabitation.
 *
 * Behaviour:
 *   - Insights / Copilot / Chat (registry) / Composer icon click:
 *     * if rail closed → open it AND set active surface to this icon
 *     * if rail open and active surface is this icon → close rail
 *     * if rail open and active surface is another icon → switch
 *   - Chat icon (legacy fallback): toggles the chat drawer overlay;
 *     opens the rail if needed so the drawer has a parent slot to
 *     absolute-fill.
 *   - Visual state of each icon reflects: rail open AND active matches.
 *
 * @component_role: Chrome
 * @component_type: Layout Affordance
 * @domain: Frontend Shell
 * @layer: presentation
 *
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 */

import { Boxes, DollarSign, MessageSquare, PanelRight, ShoppingCart, Sparkle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useSummonStateStore } from '@/lib/stores/summon-state-store';
import { useAffordance, useCopilotDefault, useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useRailSurfaceStore, type RailSurface } from '@/lib/stores/rail-surface-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCartStore } from '@/lib/stores/cart-store';
import { testIds } from '@/lib/test-utils/selectors';

export function EdgeStrip() {
  const activeMode = useLayoutStore((s) => s.activeExplorerView);
  const showSecondarySidebar = useLayoutStore((s) => s.showSecondarySidebar);
  const chatDrawerOpen = useLayoutStore((s) => s.chatDrawerOpen);
  const setChatDrawerOpen = useLayoutStore((s) => s.setChatDrawerOpen);
  const setCopilotOpenPerMode = useSummonStateStore((s) => s.setCopilotOpen);
  const copilotDefault = useCopilotDefault(activeMode);
  // Affordance addresses — derived from @affordance decorations on the
  // Python side, shipped via the matrix wire payload. Components
  // render `data-testid={...}`; the address grammar lives in
  // klyde_edge/ui_config/composition/affordances/.
  const askKlydeTestId = useAffordance('shell.edge_strip.ask_klyde');
  const composerTestId = useAffordance('shell.edge_strip.composer');

  // Legacy Copilot icon — shown only when the current (mode, selection)
  // actually declares copilot `tabs`. Registry-driven modes carry none,
  // so the icon is absent on the cards-everywhere modes. Per (mode,
  // selection): on a legacy mode the icon appears once an entity is
  // selected (the no-selection default carries no tabs).
  const { contentSelection } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  const matrix = useTotalMatrixStore((s) => s.matrix);
  const copilotCfg = matrix ? getViewportConfig(activeMode, contentSelection) : null;
  const hasCopilot = (copilotCfg?.copilot?.length ?? 0) > 0;
  // Chat — registry-driven peer when this (mode, selection) declares
  // chat_surface_cards; legacy overlay otherwise. Both paths live in
  // RailColumn; the icon handler branches on this same predicate.
  const hasChatCards = (copilotCfg?.chat_surface_cards?.length ?? 0) > 0;

  const activeSurface = useRailSurfaceStore((s) => s.active);
  const setActiveSurface = useRailSurfaceStore((s) => s.setActive);

  // Composer — third peer rail surface, swapped via setActiveSurface
  // (same as Insights / Copilot). Opt-in per mode via matrix
  // `composer_surface_cards`. The legacy floating cart drawer is
  // retired — modes that declare no composer_surface_cards (mode5:
  // sellers don't broadcast/cart; mode6/mode7: not cart domains) simply
  // have no cart affordance, which matches what those modes' own specs
  // already say. The obeya RFQ-comparison "Award Vendor" flow (mode1,
  // already composer_surface_cards-eligible) opens this same surface
  // directly via openComposerSurface — no drawer fallback needed.
  const getModeConfig = useTotalMatrixStore((s) => s.getModeConfig);
  const composerEligible =
    (getModeConfig(activeMode)?.composer_surface_cards?.length ?? 0) > 0;
  const cartItemCount = useCartStore((s) => s.items.length);

  // Cost Plan — peer rail surface showing the pinned cost plan's line
  // items. Opt-in per mode via matrix `cost_plan_surface_cards`
  // (mode3/mode11 today), same mode-level eligibility shape as
  // Composer — the pin isn't selection-scoped.
  const costPlanEligible =
    (getModeConfig(activeMode)?.cost_plan_surface_cards?.length ?? 0) > 0;

  if (copilotDefault === 'absent') {
    return null;
  }

  // Generic surface-toggle for Insights / Copilot. If clicking the
  // icon for the currently-active surface while the rail is open,
  // close the rail. Otherwise: ensure rail is open AND set this
  // surface as active.
  const handleSurfaceToggle = (surface: RailSurface) => {
    if (showSecondarySidebar && activeSurface === surface) {
      // Toggle off
      setCopilotOpenPerMode(activeMode, false);
      useLayoutStore.setState({ showSecondarySidebar: false });
      return;
    }
    setActiveSurface(surface);
    if (!showSecondarySidebar) {
      setCopilotOpenPerMode(activeMode, true);
      useLayoutStore.setState({ showSecondarySidebar: true });
    }
  };

  const handleChatToggle = () => {
    // Registry-driven path — chat is a peer rail surface. Same
    // open/close semantics as Insights / Copilot / Composer.
    if (hasChatCards) {
      handleSurfaceToggle('chat');
      return;
    }
    // Legacy overlay path — kept for modes that haven't declared
    // chat_surface_cards in the matrix. Toggles the chat drawer
    // overlay; opens the rail if needed so the drawer has a parent
    // slot to absolute-fill.
    if (!chatDrawerOpen) {
      if (!showSecondarySidebar) {
        setCopilotOpenPerMode(activeMode, true);
        useLayoutStore.setState({ showSecondarySidebar: true });
      }
      setChatDrawerOpen(true);
    } else {
      setChatDrawerOpen(false);
    }
  };

  const isInsightsActive = showSecondarySidebar && activeSurface === 'insights';
  const isAskActive = showSecondarySidebar && activeSurface === 'ask';
  const isComposeActive = showSecondarySidebar && activeSurface === 'compose';
  const isCopilotActive = showSecondarySidebar && activeSurface === 'copilot';
  // Chat active state — for migrated modes, reflect peer-active;
  // for legacy modes, reflect the overlay's chatDrawerOpen flag.
  const isChatActive = hasChatCards
    ? showSecondarySidebar && activeSurface === 'chat'
    : chatDrawerOpen;
  const isComposerActive = showSecondarySidebar && activeSurface === 'composer';
  const isCostPlanActive = showSecondarySidebar && activeSurface === 'cost_plan';

  return (
    <div
      className={cn(
        'flex w-12 flex-col items-center gap-1 border-l border-border bg-sidebar py-2',
        'shrink-0',
      )}
      role="toolbar"
      aria-label="Panel summon strip"
    >
      <Button
        variant="ghost"
        size="icon"
        className={cn('h-12 w-12 [&_svg]:size-6', isInsightsActive && 'bg-muted text-primary')}
        onClick={() => handleSurfaceToggle('insights')}
        aria-pressed={isInsightsActive}
        aria-label={isInsightsActive ? 'Close Insights' : 'Open Insights'}
        title={isInsightsActive ? 'Close Insights' : 'Open Insights'}
      >
        <Sparkles className="h-6 w-6" />
      </Button>

      {/* Ask — agent rail surface. Always available when the rail
          itself is (i.e. whenever the EdgeStrip renders). Parallel to
          mode8's canvas-resident ask input; the rail surface keeps the
          agent reachable from every mode without owning canvas real
          estate. ki_ask is the BFF strategy behind both. */}
      <Button
        variant="ghost"
        size="icon"
        className={cn('h-12 w-12 [&_svg]:size-6', isAskActive && 'bg-muted text-primary')}
        onClick={() => handleSurfaceToggle('ask')}
        aria-pressed={isAskActive}
        aria-label={isAskActive ? 'Close Ask Klyde' : 'Open Ask Klyde'}
        title={isAskActive ? 'Close Ask Klyde' : 'Open Ask Klyde'}
        data-testid={askKlydeTestId}
      >
        <Sparkle className="h-6 w-6" />
      </Button>

      {/* Ask (composed) — the klyde_ask compositor surface. Parallel to the
          docs-backed Ask icon above; this one federates knowledge / capability
          / navigate lanes. Ships beside it so the working path is untouched. */}
      <Button
        variant="ghost"
        size="icon"
        className={cn('h-12 w-12 [&_svg]:size-6', isComposeActive && 'bg-muted text-primary')}
        onClick={() => handleSurfaceToggle('compose')}
        aria-pressed={isComposeActive}
        aria-label={isComposeActive ? 'Close Ask Klyde (composed)' : 'Open Ask Klyde (composed)'}
        title={isComposeActive ? 'Close Ask Klyde (composed)' : 'Open Ask Klyde (composed)'}
      >
        <Boxes className="h-6 w-6" />
      </Button>

      {hasCopilot && (
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-12 w-12 [&_svg]:size-6', isCopilotActive && 'bg-muted text-primary')}
          onClick={() => handleSurfaceToggle('copilot')}
          aria-pressed={isCopilotActive}
          aria-label={isCopilotActive ? 'Close Copilot' : 'Open Copilot'}
          title={isCopilotActive ? 'Close Copilot' : 'Open Copilot'}
        >
          <PanelRight className="h-6 w-6" />
        </Button>
      )}

      <Button
        variant="ghost"
        size="icon"
        className={cn('h-12 w-12 [&_svg]:size-6', isChatActive && 'bg-muted text-primary')}
        onClick={handleChatToggle}
        aria-pressed={isChatActive}
        aria-label={isChatActive ? 'Close Chat' : 'Open Chat'}
        title={isChatActive ? 'Close Chat' : 'Open Chat'}
      >
        <MessageSquare className="h-6 w-6" />
      </Button>

      {composerEligible && (
        <Button
          data-testid={composerTestId}
          variant="ghost"
          size="icon"
          className={cn('h-12 w-12 relative [&_svg]:size-6', isComposerActive && 'bg-muted text-primary')}
          onClick={() => handleSurfaceToggle('composer')}
          aria-pressed={isComposerActive}
          aria-label={isComposerActive ? 'Close Composer' : 'Open Composer'}
          title={isComposerActive ? 'Close Composer' : 'Open Composer'}
        >
          <ShoppingCart className="h-6 w-6" />
          {cartItemCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center rounded-full bg-primary text-[9px] font-medium text-primary-foreground leading-none">
              {cartItemCount > 99 ? '99+' : cartItemCount}
            </span>
          )}
        </Button>
      )}

      {costPlanEligible && (
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-12 w-12 [&_svg]:size-6', isCostPlanActive && 'bg-muted text-primary')}
          onClick={() => handleSurfaceToggle('cost_plan')}
          aria-pressed={isCostPlanActive}
          aria-label={isCostPlanActive ? 'Close Cost Plan' : 'Open Cost Plan'}
          title={isCostPlanActive ? 'Close Cost Plan' : 'Open Cost Plan'}
        >
          <DollarSign className="h-6 w-6" />
        </Button>
      )}
    </div>
  );
}
